import { isEmpty, forOwn, get, isNil } from "lodash";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AuthState, useAuth } from "@/context/auth-context";
import { UploadClient } from "@uploadcare/upload-client";
import { Profile } from "@/features/profile/utils/types";
import toast from "react-hot-toast";
import sanitizeHtml from "sanitize-html";
import parse from "html-react-parser";
import { TALIBON_ADDRESS_INITIAL_VALUES } from "@/features/constants";

export const transformIncluded = (x: any, included: any) => {
  if (!included || isEmpty(included)) return x;
  const rowIncluded: any = {};
  forOwn(x.relationships, (v: any, k: any) => {
    rowIncluded[k] = Array.isArray(v.data)
      ? v.data.map(
          (z: any) =>
            included.find((y: any) => y.type === get(z, "type") && y.id === get(z, "id")) || {}
        )
      : included.find((y: any) => y.type === get(v, "data.type") && y.id === get(v, "data.id")) ||
        {};
  });
  const { _links, _relationships, _type, ...rest } = x;
  return { ...rest, included: rowIncluded };
};

export const transformIncludedDeep = (x: any, included: any) => {
  if (!included || isEmpty(included)) return x;
  const rowIncluded: any = {};
  forOwn(x.relationships, (v: any, k: any) => {
    rowIncluded[k] = Array.isArray(v.data)
      ? v.data.map((z: any) =>
          transformIncludedDeep(
            included.find((y: any) => y.type === get(z, "type") && y.id === get(z, "id")) || {},
            included
          )
        )
      : transformIncludedDeep(
          included.find((y: any) => y.type === get(v, "data.type") && y.id === get(v, "data.id")) ||
            {},
          included
        );
  });
  const { _links, _relationships, _type, ...rest } = x;
  return { ...rest, included: rowIncluded };
};

export const fromNow = (date: string | Date) => {
  dayjs.extend(relativeTime);
  return dayjs(date).fromNow();
};

export const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const charCount = (str) => {
  let numOfChars = 0;
  for (let i = 0; i < str.length; i++) {
    numOfChars++;
  }
  return numOfChars;
};

type DateFormat = "SLASHED" | "SHORT_WITH_SLASHED_INPUT" | "SHORT";

export function convertDateFormat(inputDate: string, format: DateFormat) {
  if (!inputDate) return;
  if (format === "SLASHED") {
    const months = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12"
    };

    const [month, day, year] = inputDate.split(/[\s,]+/);
    return `${months[month]}/${day.padStart(2, "0")}/${year}`;
  }
  if (format === "SHORT_WITH_SLASHED_INPUT") {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];

    const [month, day, year] = inputDate.split("/");
    const monthName = months[parseInt(month, 10) - 1];
    return `${monthName} ${day}, ${year}`;
  }
  if (format === "SHORT") {
    const date = new Date(inputDate);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
    return formattedDate;
  }
  return inputDate;
}

export const capitalize = (text: string) => {
  if (!text) return text;
  return text?.charAt(0).toUpperCase() + text?.slice(1).toLowerCase();
};

export const handleOpenPgiUrl = (pgiUrl, callback) => {
  const title = "PAYMENT GATEWAY";
  const h = 675;
  const w = 775;
  // Fixes dual-screen position                         Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  // ? window.innerWidth
  // : document.documentElement.clientWidth
  // ? document.documentElement.clientWidth
  // : screen.width;

  const height =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.documentElement.clientHeight ||
    window.screen.height;
  // ? window.innerHeight
  // : document.documentElement.clientHeight
  // ? document.documentElement.clientHeight
  // : screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;
  const newWindow = window.open(
    pgiUrl,
    title,
    `scrollbars=yes, width=${w / systemZoom}, height=${h / systemZoom}, top=${top}, left=${left}`
  );

  const timer = setInterval(function () {
    if (newWindow.closed) {
      clearInterval(timer);
      if (callback) callback();
    }
  }, 1000);

  if (window.focus && newWindow) newWindow.focus();
};

export const METHODS = [
  {
    id: 1,
    selectedSrc: "/images/mfa/auth-selected.png",
    src: "/images/mfa/auth-default.png",
    label: "Authenticator App",
    label2: "Google Authenticator",
    value: "authenticator_app",
    URL: "/login/authenticator",
    description: "Enter a pin provided by your preferred authenticator"
  },
  {
    id: 2,
    selectedSrc: "/images/mfa/sms-selected.png",
    src: "/images/mfa/sms-default.png",
    label: "SMS Authentication",
    label2: "SMS",
    value: "sms_otp",
    URL: "/login/auth-otp",
    description: "Enter a one-time pin sent to your registered mobile number"
  },
  {
    id: 3,
    selectedSrc: "/images/mfa/email-selected.png",
    src: "/images/mfa/email-default.png",
    label: "Email Authentication",
    label2: "Email",
    value: "email_otp",
    URL: "/login/auth-otp",
    description: "Enter a one-time pin sent to your registered email address"
  }
];

export function WithAuth({
  children
}: {
  children: (authState: AuthState | null) => React.ReactNode;
}) {
  const { authState } = useAuth();
  return children(authState);
}

export const getAlphabetSuffix = (index: number) => {
  let suffix = "";
  while (index >= 0) {
    suffix = String.fromCharCode(65 + (index % 26)) + suffix;
    index = Math.floor(index / 26) - 1;
  }
  return suffix;
};

const client = new UploadClient({ publicKey: process.env.NEXT_PUBLIC_APP_UCARE_PUB_KEY });

export async function fetchFileInfo(uuid: string) {
  try {
    const fileInfo = await client.info(uuid);
    return fileInfo;
  } catch {
    return null;
  }
}

export const hypenToEmpty = (str: string) => {
  if (typeof str !== "string") return str;
  return (str || "").replace("-", "");
};
export const emptyToHyphen = (str: string) => (str === "" ? "-" : str);
export const hypenToNull = (str: string) => (str === "-" ? null : str);

export const formatNumberWithCommas = (number?: number) => {
  if (!number) return;
  return Number(number).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const filterProfile = (profile: Profile | null | undefined) => {
  if (!profile || typeof profile !== "object") return {};
  return Object.fromEntries(
    Object.entries(profile).filter(
      ([_, value]) => value !== null && value !== undefined && value !== ""
    )
  );
};

export const downloadFile = async (file_url: string, name: string) => {
  try {
    const a = document.createElement("a");
    a.href = file_url;
    a.download = file_url.split("/").pop() || name;
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(file_url);
    document.body.removeChild(a);
  } catch (error) {
    toast.error("Error downloading file:", error);
  }
};

export const fetchAndDownloadImage = async (file_url: string, name: string) => {
  try {
    const response = await fetch(file_url);
    if (!response.ok) throw new Error("Failed to download image");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = file_url.split("/").pop() || name;
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    toast.error("Error downloading file:", error);
  }
};

export const getOrdinalIndicator = (num: number): string => {
  if (num === null || num === undefined) return "";

  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
};

type CurrencyDisplay = "symbol" | "code" | "name" | "narrowSymbol";

export const formatCurrency = (
  amount: number,
  currency = "PHP",
  locale = "en-US",
  currencyDisplay: CurrencyDisplay = "code"
) => {
  if (isNaN(amount) || amount === null || typeof amount === "undefined") return null;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const parseHTML = (html: string) => {
  if (!html) return null;
  const sanitized = sanitizeHtml(html, {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, "img", "iframe"],
    allowedAttributes: {
      "*": ["style", "class"],
      img: ["src", "alt", "width", "height"],
      iframe: ["src", "width", "height", "frameborder", "allow", "allowfullscreen"]
    },
    transformTags: {
      "*": (tagName, attribs) => {
        const cleanAttribs: any = {};
        for (const attr in attribs) {
          if (/^[a-zA-Z_:][a-zA-Z0-9:._-]*$/.test(attr)) {
            cleanAttribs[attr] = attribs[attr];
          }
        }
        return { tagName, attribs: cleanAttribs };
      }
    }
  });
  return parse(sanitized);
};

export const handleApiError = (
  error: any,
  defaultMessage: string = "Something went wrong. Please try again."
) => {
  // The API answers in two shapes: `{ errors: [{ detail }] }` and
  // `{ message }`. Fall back to the default for the third case — no response
  // at all — so a network failure never renders an empty toast.
  const data = error?.response?.data;
  const firstError = Object.values(data?.errors ?? {})[0] as any;

  let errorMessage = defaultMessage;
  if (typeof firstError === "string") {
    errorMessage = firstError;
  } else if (firstError && typeof firstError === "object") {
    errorMessage = firstError["detail"] ?? defaultMessage;
  } else if (data?.message) {
    errorMessage = data.message;
  }

  toast.error(errorMessage);
};

export const normalizeObject = <T extends Record<string, any>>(obj: T, fallback: any = ""): T => {
  return Object.fromEntries(
    Object.entries(obj || {}).map(([key, value]) => [key, isNil(value) ? fallback : value])
  ) as T;
};

export const normalizeArray = <T,>(arr: T[], fallback: any = ""): T[] => {
  return arr.map((item) => (isNil(item) ? fallback : item));
};

export const storeMfaExpiry = (duration: number) => {
  const newExpiry = Date.now() + duration;
  sessionStorage.setItem("mfaExpiry", String(newExpiry));
};

export const isTalibonAddressFn = ({
  regionCode,
  provinceCode,
  municipalityCode
}: {
  regionCode: string;
  provinceCode: string;
  municipalityCode: string;
}): boolean => {
  return (
    regionCode === TALIBON_ADDRESS_INITIAL_VALUES.regionCode &&
    provinceCode === TALIBON_ADDRESS_INITIAL_VALUES.provinceCode &&
    municipalityCode === TALIBON_ADDRESS_INITIAL_VALUES.municipalityCode
  );
};
